import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { PayPeriodSettings, PayPeriod } from '../types';

export const calculateNextPayPeriods = (count: number = 4, offset: number = 0, settings?: PayPeriodSettings | null): PayPeriod[] => {
  if (!settings) return [];

  // Parse the period start date properly to avoid timezone issues
  const periodStartDate = new Date(settings.periodStart + 'T00:00:00');
  
  // Calculate days per period
  let daysPerPeriod: number;
  switch (settings.frequency) {
    case 'weekly':
      daysPerPeriod = 7;
      break;
    case 'biweekly':
      daysPerPeriod = 14;
      break;
    case 'semimonthly':
      daysPerPeriod = 15; // Approximate
      break;
    case 'monthly':
      daysPerPeriod = 30; // Approximate
      break;
    default:
      daysPerPeriod = 14;
  }
  // Calculate the current period number based on today's date
  const today = new Date();
  const todayAtMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysSinceStart = Math.floor((todayAtMidnight.getTime() - periodStartDate.getTime()) / (24 * 60 * 60 * 1000));
  const currentPeriodIndex = Math.floor(daysSinceStart / daysPerPeriod);
  
  // Calculate the starting period index (current + offset)
  const startingPeriodIndex = currentPeriodIndex + offset;

  const periods: PayPeriod[] = [];

  // Generate consecutive periods
  for (let i = 0; i < count; i++) {
    const periodIndex = startingPeriodIndex + i;
    
    // Calculate the start date for this period (avoid setDate issues)
    const startTime = periodStartDate.getTime() + (periodIndex * daysPerPeriod * 24 * 60 * 60 * 1000);
    const currentStart = new Date(startTime);
    
    // Calculate the end date (period length - 1 day)
    const endTime = startTime + ((daysPerPeriod - 1) * 24 * 60 * 60 * 1000);
    const periodEnd = new Date(endTime);

    periods.push({
      start: currentStart.toISOString().split('T')[0],
      end: periodEnd.toISOString().split('T')[0]
    });
  }

  return periods;
};

export function usePayPeriodSettings() {
  const [settings, setSettings] = useState<PayPeriodSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    let attempts = 0;
    const maxAttempts = 3;
    const backoffMs = 1000; // Start with 1 second

    try {
      setLoading(true);
      setError(null);

      while (attempts < maxAttempts) {
        try {
          const { data, error } = await supabase
            .from('pay_period_settings')
            .select('*')
            .limit(1)
            .single();

          if (error) throw error;

          if (data) {
            setSettings({
              id: data.id,
              frequency: data.frequency,
              periodStart: data.period_start,
              payday: data.payday,
              lastProcessed: data.last_processed
            });
          }
          break;
        } catch (err) {
          attempts++;
          if (attempts === maxAttempts) throw err;
          
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, backoffMs * attempts));
        }
      }
    } catch (err) {
      console.error('Error loading pay period settings:', err);
      setError(
        err instanceof Error 
          ? `Failed to load pay period settings: ${err.message}` 
          : 'Failed to load pay period settings. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const getPeriodNumber = (date: Date): number => {
    if (!settings) return 0;
    
    const periodStartDate = new Date(settings.periodStart);
    const targetDate = new Date(date);
    
    // Calculate days per period
    let daysPerPeriod: number;
    switch (settings.frequency) {
      case 'weekly':
        daysPerPeriod = 7;
        break;
      case 'biweekly':
        daysPerPeriod = 14;
        break;
      case 'semimonthly':
        daysPerPeriod = 15; // Approximate
        break;
      case 'monthly':
        daysPerPeriod = 30; // Approximate
        break;
      default:
        daysPerPeriod = 14;
    }
    
    // Calculate days since the period start
    const daysSinceStart = Math.floor((targetDate.getTime() - periodStartDate.getTime()) / (24 * 60 * 60 * 1000));
    

    // Calculate which period this date falls into (0-based)
    const periodIndex = Math.floor(daysSinceStart / daysPerPeriod);
    
    // Convert to 1-based period number and handle year wrapping
    let periodNumber = (periodIndex % 52) + 1;
    
    // Handle negative periods (dates before the start date)
    if (daysSinceStart < 0) {
      periodNumber = 52 + ((periodIndex % 52) + 1);
    }

    return periodNumber;
  };

  const updateLastProcessed = async (date: string) => {
    try {
      const { error } = await supabase
        .from('pay_period_settings')
        .update({ last_processed: date })
        .eq('id', settings?.id);

      if (error) throw error;

      setSettings(prev => prev ? {
        ...prev,
        lastProcessed: date
      } : null);
    } catch (err) {
      console.error('Error updating last processed date:', err);
      throw err;
    }
  };

  return {
    settings,
    loading,
    error,
    calculateNextPayPeriods: (count: number = 4, offset: number = 0) => calculateNextPayPeriods(count, offset, settings),
    getPeriodNumber,
    updateLastProcessed,
    refetch: fetchSettings
  };
}